import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { LegendComponent } from "../../../../../common/components/legend_component";
import { LegendIcon } from "../../../../../common/components/legend_icon";
import { LegendItemComponent } from "../../../../../common/components/legend_item_component";
import { useObrasConcretagemViewModel } from "../../viewModels/obras_concretagem_view_model";
import { Header } from "./components/header";
import { useMemo } from "react";
import { GenerateObraConcretagemColumnDefinition } from "./columns";
import { MapaConcretagem } from "../../../domain/models/mapa_concretagem";
import { GridPinnedColumnFields } from "@mui/x-data-grid-pro";
import { MapaConcretagemCopyModal } from "./components/mapa_concretagem_copy_modal";
import { ObrasReportModals } from "../../components/obras_report_modals";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { ObrasTopHeader } from "../../components/obras_top_header";
import { ObrasTopBar } from "../../components/obras_top_bar";
import { Link as LinkTo } from 'react-router';
import { StyledDataGrid } from "./components/styled_data_grid";
import { MapaConcretagemAprovFinalModal } from "./components/mapa_concretagem_aprov_final_modal";

import AlertError from "../../../../../common/components/alert_error";
import DeleteDialog from "../../../../../common/components/delete_dialog";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";

/**
 * ObrasConcretagem
 */

export const ObrasConcretagem = () => {
  const viewModel = useObrasConcretagemViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateObraConcretagemColumnDefinition({
      onActionEditar: viewModel.handleMapaConcretagemOpenStateModal,
      onActionDeletar: viewModel.handleDeleteMapaConcretagemOpenStateModal,
      onActionCopiar: viewModel.handleMapaConcretagemCopyOpenStateModal,
    });
  }, [
    viewModel.handleMapaConcretagemOpenStateModal,
    viewModel.handleDeleteMapaConcretagemOpenStateModal,
    viewModel.handleMapaConcretagemCopyOpenStateModal,
  ]);

  const pinnedColumns = useMemo((): GridPinnedColumnFields => {
    return viewModel.mapaConcretagens.length > 0
      ? { left: ['__check__'], right: ['idMapaConcretagem'] }
      : {};
  }, [
    viewModel.mapaConcretagens,
  ]);

  return (
    <>
      {/**
        * ObrasTopBar
        */}

      <ObrasTopBar
        obrasReportState={viewModel.obrasReportState}
        state={viewModel.obrasTopHeaderState}
        selectedObra={viewModel.selectedObraHeaderInfo ?? null}
        leftReportSlotButton={
          <div className="w-[10.5rem]">
            <PrimaryButton
              buttonColor="secondary-orange"
              onClick={() => viewModel.handleAprovFinalModal(true)}>
              Aprovar Selecionados
            </PrimaryButton>
          </div>
        }
        leftBackSlotButton={
          <div className="w-[100px]">
            <PrimaryButton
              onMouseDown={e => {
                if (e.button === 1) {
                  viewModel.handleMapaConcretagemOpenStateModal(null, true);
                } else {
                  viewModel.handleMapaConcretagemOpenStateModal(null, e.ctrlKey);
                }
                e.preventDefault();
              }}
              startIcon={<DynamicIcon nameIcon="Add" size={25} className="text-white" />}>
              Novo
            </PrimaryButton>
          </div>
        }
        onBack={viewModel.handleBack}>
      </ObrasTopBar>

      <div className="flex w-full gap-2 flex-col px-4 pt-2 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit" component={'span'}>
            <LinkTo to='/Obras_lista'>Lista de Obras</LinkTo>
          </Link>
          <Typography className="text-primary-green">
            Mapa de Concretagem
          </Typography>
        </Breadcrumbs>

        {/**
          * ObrasTopHeader
          */}

        <ObrasTopHeader
          title="Mapa de Concretagem"
          selectedObra={viewModel.selectedObraHeaderInfo ?? null}
          onReload={viewModel.handleReload}
        />

        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload}
                />
              </div>
            ) : (
              <>
                <Header
                  dataDe={viewModel.filters.dataDe}
                  dataAte={viewModel.filters.dataAte}
                  localLancamento={viewModel.filters.localLancamento}
                  resistencia={viewModel.filters.resistencia}
                  nroNotaFiscal={viewModel.filters.nroNotaFiscal}
                  statusRompimento={viewModel.filters.statusRompimento}
                  enviadoLab={viewModel.filters.enviadoLab}
                  nroLaudo={viewModel.filters.nroLaudo}
                  aprovado={viewModel.filters.aprovado}
                  onDataDeChange={viewModel.handleDataDeChange}
                  onDataAteChange={viewModel.handleDataAteChange}
                  onLocalLancamentoChange={viewModel.handleLocalLancamentoChange}
                  onResistenciaChange={viewModel.handleResistenciaChange}
                  onNroNotaFiscalChange={viewModel.handleNroNotaFiscalChange}
                  onNroLaudoChange={viewModel.handleNroLaudoChange}
                  onStatusRompimentoChange={viewModel.handleStatusRompimentoChange}
                  onEnviadoLabChange={viewModel.handleEnviadoLabChange}
                  onAprovadoChange={viewModel.handleAprovadoChange}
                  onClearFilter={viewModel.handleClearFilter}
                />

                <Stack spacing={0}>
                  <div className="w-full flex flex-row justify-between items-stretch">
                    <div className="flex flex-col flex-wrap">
                      <LegendComponent label="Status">
                        <LegendItemComponent
                          icon={<LegendIcon color='green' title='RD' />}
                          label='Rompimento em dia' />

                        <LegendItemComponent
                          icon={<LegendIcon color='red' title='RA' />}
                          label='Rompimento Atrasado' />
                      </LegendComponent>
                    </div>

                    <div className="flex flex-col justify-between flex-1">
                      <TablePagination
                        component="div"
                        count={viewModel.pagination.totalItems}
                        page={viewModel.pagination.page - 1}
                        onPageChange={(_, p) => viewModel.handlePageChange(p)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        rowsPerPage={viewModel.pagination.rowsPerPage}
                        onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />
                    </div>
                  </div>

                  <StyledDataGrid
                    tableName="obras_concretagem"
                    disableColumnMenu={false}
                    checkboxSelection={true}
                    textSize="muitoPequeno"
                    isRowSelectable={({ row }) => row['aprovFinalDate'] === null && row['aprovFinalIdFuncionario'] === null}
                    rowSelectionModel={viewModel.rowSelectionModel}
                    disableRowSelectionOnClick={true}
                    onRowSelectionModelChange={viewModel.handleAprovFinalSelectChange}
                    getRowId={(row: MapaConcretagem) => row.idMapaConcretagem}
                    getRowHeight={() => 'auto'}
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    rows={viewModel.mapaConcretagens}
                    rowCount={viewModel.mapaConcretagens.length}
                    pinnedColumns={pinnedColumns}
                    disableColumnSorting={false}
                    sortingMode="server"
                    onSortModelChange={viewModel.handleSortingModelChange}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />

                </Stack>
              </>
            )
        }

        {/**
          * Mapa Concretagem Modal
          */}

        <MapaConcretagemAprovFinalModal
          open={viewModel.aprovFinalModal}
          saving={viewModel.saving}
          loading={viewModel.loading}
          idFuncionario={viewModel.idFuncionarioAprovacao}
          funcionarioDropdown={viewModel.funcionariosDropdown}
          onClose={() => viewModel.handleAprovFinalModal(false)}
          onSubmit={viewModel.handleAprovFinalSubmit}
        />

        <MapaConcretagemCopyModal
          open={viewModel.openMapaConcretagemCopyModal}
          saving={viewModel.saving}
          loading={viewModel.loading}
          onClose={viewModel.handleMapaConcretagemCopyOpenStateModal}
          onSubmit={viewModel.handleMapaConcretagemCopyConfirm}
        />

        <DeleteDialog
          open={viewModel.openMapaConcretagemDeleteModal}
          saving={viewModel.saving}
          title='Você realmente deseja excluir este item?'
          onClose={() => viewModel.handleDeleteMapaConcretagemOpenStateModal(null, false)}
          onDelete={() => viewModel.handleDeleteMapaConcretagemConfirm()}
        />

        {/**
          * Relatorios obras.
          */}

        <ObrasReportModals
          state={viewModel.obrasReportState}
        />

        {/**
          * Modal historico de alterações.
          */}

        <ChangeHistoryModal
          changeHistoryHandler={viewModel.changeHistory}
        />

      </div>
    </>
  )
}